import { reducedMotion$, reveal, type RevealSettledEvent } from '@schmancy/directives'
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { deepActiveElement } from '@schmancy/utils/focus'
import { mutationObserver } from '@schmancy/utils/rxjs-utils/mutation-observer'
import { css, html, type PropertyValues, type TemplateResult } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { keyed } from 'lit/directives/keyed.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import {
	animationFrames,
	catchError,
	concat,
	EMPTY,
	filter,
	from,
	fromEvent,
	ignoreElements,
	merge,
	Observable,
	of,
	share,
	skip,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from 'rxjs'

import '@schmancy/primitives/button/button'
import '@schmancy/primitives/surface/surface'
import '@schmancy/primitives/typography/typography'
import '@schmancy/primitives/visually-hidden/visually-hidden'

/** The collapse before a REPLACEMENT notice re-opens — long enough for the eye
 *  to register that the panel left, short enough that the two reads feel like
 *  one gesture. Skipped for a first raise (nothing to leave) and under reduced
 *  motion. */
const REPLACE_COLLAPSE_MS = 160

/** Keys a UA scrolls the focused scroller with. Space is in the set: on a page
 *  with nothing text-editable focused it pages down. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

/** `nearest`, because `end` aligns unconditionally and would drag an element
 *  with room to spare toward the fold; `instant`, because `overflowWithin` pins
 *  `scroll-behavior: smooth` on the scroller, which `auto` defers to and which a
 *  per-frame scroll would spend every frame chasing. */
const PIN: ScrollIntoViewOptions = { block: 'nearest', behavior: 'instant' }

/** An outcome the reader has to hear now, whether or not they are still at the
 *  control it reports on. `success` and `info` wait for a pause instead. */
const ASSERTIVE_TONES = new Set<SchmancyAlertTone>(['error', 'warning'])

/** The reader taking the scroll over. `touchmove`, never `touchstart`: a finger
 *  that lands without travelling past the slop threshold is an impatient second
 *  tap on the action, and forfeiting the repair for it would strand the very
 *  reader who is trying to reach it. */
const userScroll$ = merge(
	fromEvent(window, 'wheel', { passive: true }),
	fromEvent(window, 'touchmove', { passive: true }),
	fromEvent<KeyboardEvent>(window, 'keydown').pipe(filter(e => SCROLL_KEYS.has(e.key))),
)

/** Whether `node` is `container` or inside it, walked up the composed tree —
 *  shadow root to host — because the focused node usually lives inside some
 *  component's shadow tree, where the container's own `contains()` cannot see. */
const composedWithin = (node: Node | null, container: Node | null): boolean => {
	for (let at = node; at !== null; at = at instanceof ShadowRoot ? at.host : at.parentNode) {
		if (at === container) return true
	}
	return false
}

/** The role tint the alert paints, and the thing it claims about the outcome
 *  it reports. Maps 1:1 onto `<schmancy-surface type=…>`. */
export type SchmancyAlertTone = 'error' | 'warning' | 'success' | 'info'

/** One raised notice. A fresh object per raise: the alert never rewrites the
 *  words of a panel already on screen (see `raise`). */
export interface SchmancyAlertNotice {
	tone: SchmancyAlertTone
	/** Names what happened, in the reader's own terms — the first thing read. */
	headline: string
	/**
	 * Why, and what to do next. Optional, because some refusals arrive as exactly
	 * one sentence and there is no second one to write: a server's own rejection
	 * text reaches `<schmancy-form>` as a single `FormError.message`, and padding
	 * it out with a generic "please try again" would be invented copy. Absent ⇒
	 * the panel is the headline alone, with no empty line under it.
	 */
	body?: string
	/** The in-context affordance that satisfies the unmet precondition, when
	 *  the outcome has one. Present ⇒ the alert ships a button that dispatches
	 *  `remedy`; absent ⇒ no action lives inside the alert. */
	remedy?: string
}

/**
 * `remedy` — the reader pressed the alert's own in-context action. The notice
 * that carried it rides the detail, so a host raising more than one kind of
 * blocked outcome can tell which remedy was asked for.
 *
 * Bubbles within the tree it was dispatched in, never across a shadow boundary
 * — the same answer `reveal-settled` gives, for the same reason. `remedy` is a
 * one-word name on the global event map, and the alert's consumer binds
 * `@remedy` on the `<schmancy-alert>` tag itself, in the tree that owns it;
 * `composed: true` buys that consumer nothing and lets a generically-named
 * event escape every shadow root up to `document`, where the next component to
 * pick the same word collides with it.
 */
export class SchmancyAlertRemedyEvent extends CustomEvent<{ notice: SchmancyAlertNotice }> {
	constructor(notice: SchmancyAlertNotice) {
		super('remedy', { detail: { notice }, bubbles: true, composed: false })
	}
}

/**
 * An inline notice that reports a submission-level outcome in place — the role
 * `$notify`'s transient corner toast is deliberately wrong for, because this
 * state persists until the reader changes something.
 *
 * Raising is imperative, because a submission outcome is an EVENT rather than a
 * state: `alert.raise({ tone, headline, body, remedy })` and `alert.clear()`.
 * A declarative `.notice=${…}` property would need either an identity compare
 * (which re-raises on every render that rebuilds the object literal) or a value
 * compare (which swallows a second, identical refusal — precisely the event the
 * reader most needs to hear about), so the API takes the call instead of a
 * counter the consumer has to maintain.
 *
 * On raise it runs one choreography, in this order, because each step buys the
 * next one room:
 *
 *   1. blurs whatever is focused — when focus is inside `reportsOn` (the
 *      control the notice reports on), inside the alert, or on nothing. A
 *      refused submission is not the field's fault, and on a phone the software
 *      keyboard is between a third and a half of the screen; dropping it is the
 *      largest visibility win available. A notice that arrives LATE, for a
 *      control the reader has since left, leaves their focus where it is:
 *      blurring a field that commits on blur saves whatever is half-typed there.
 *   2. springs open (`reveal`, which owns the reduced-motion branch).
 *   3. holds `anchor` in view for EVERY frame the panel is growing, so the
 *      element it names never leaves the screen and never has to be moved back.
 *      Where focus is gets asked on every one of those frames: focus that has
 *      left the scope holds the panel and then the focused element in view
 *      instead, so the reader's caret stays on screen and wins when both do not
 *      fit. The page is never scrolled while it sits behind an open modal. A
 *      reader who scrolls during the growth has taken over: the pin stops that
 *      frame and does not resume for this notice.
 *   4. once the spring has settled and nothing above the alert is `inert` (the
 *      page behind an open modal is), takes focus inside the panel if focus is
 *      still in scope, so the next Tab lands on the remedy. Otherwise focus is
 *      left alone, and the next time it comes back into the scope the panel is
 *      brought into view once.
 *
 * The voice follows the tone, never the focus: `error` and `warning` are
 * `role="alert"`, time-sensitive whether or not the reader is still at the
 * control; `success` and `info` are a group named by the headline, said through
 * a polite live region unless the raise took focus and the named panel reads
 * itself. An `error` or `warning` whose panel is inserted behind an open modal
 * is rendered as that named group instead — a `role="alert"` inserted where no
 * one can hear it fires to no one — and is said through an assertive live
 * region once the modal has closed.
 *
 * A REPLACEMENT notice collapses and re-opens rather than swapping its text in
 * place: a second, different outcome is a second event and has to read as one.
 * Silently rewriting the words of a panel already on screen is indistinguishable
 * from a stale panel nobody updated.
 *
 * Unraised, the host is `display: contents` and the panel's box is not
 * displayed, so an unraised alert costs its container no box and no flex/grid
 * gap while its live regions — absolutely positioned — stay exposed to
 * assistive tech. Raised, the host is a block, so a class set on it applies.
 *
 * ```ts
 * html`<schmancy-alert ${ref(this.alert)} @remedy=${() => this.retry()}></schmancy-alert>`
 * // …
 * this.alert.value.anchor = this.submitButton.value
 * this.alert.value.raise({ tone: 'error', headline: 'That did not go through', body: '…' })
 * ```
 */
@schmancyElement('schmancy-alert')
export class SchmancyAlert extends SchmancyElement {
	// The box's display is flipped from `:host([raised])`, which `willUpdate`
	// sets before the commit, rather than from a class committed in the same pass
	// as `reveal`: the directive forces a reflow at its closed height during that
	// commit, and a box still undisplayed there has no height to spring from.
	static styles = [
		css`
			:host {
				display: contents;
				--schmancy-alert-box: none;
			}
			:host([raised]) {
				display: block;
				--schmancy-alert-box: block;
			}
		`,
	]

	/**
	 * The element that must stay on screen while the alert opens. The alert opens
	 * directly ABOVE it in the usual composition, so holding the ANCHOR in view
	 * is the only target that cannot push it out — centring on the alert itself
	 * can, whenever the scroll region has run out of content to scroll. Falls
	 * back to the alert's own panel.
	 */
	@property({ attribute: false }) anchor: HTMLElement | null = null

	/**
	 * The control this notice reports on — the button whose press it answers. A
	 * raise moves focus only while focus is inside it, inside the alert, or on
	 * nothing; anywhere else the reader has moved on and keeps their focus. Unset,
	 * it is `anchor`; with neither set, only focus on nothing or inside the alert
	 * is moved.
	 */
	@property({ attribute: false }) reportsOn: Element | null = null

	@state() private open = false
	@state() private shown: SchmancyAlertNotice | null = null
	/** Whether the page was reachable in the render that inserted the panel.
	 *  Decided there, not at the settle: a modal opening mid-spring does not
	 *  unsay an alert that already fired, and one closing mid-spring does not
	 *  make heard an alert that fired to no one. */
	@state() private shownReachable = true
	/** What a live region says, and which one. Emptied on every raise and written
	 *  two frames after the delivery point, so the region is exposed and empty
	 *  for at least one rendered frame before its words — a live region that
	 *  appears together with its text is routinely not announced — and an
	 *  identical second notice is still a change. */
	@state() private announcement: { notice: SchmancyAlertNotice; politeness: 'polite' | 'assertive' } | null = null

	private box = createRef<HTMLElement>()
	private panel = createRef<HTMLElement>()
	private raise$ = new Subject<SchmancyAlertNotice | null>()
	/** Whether the alert itself held focus when its collapse began. `reveal`
	 *  marks the wrapper `inert` on the way down, and a UA drops focus from an
	 *  element that becomes inert straight to `<body>` — which loses a keyboard
	 *  reader's place on the page entirely. Restored to `anchor`; never to
	 *  whatever `raise()` blurred, which was usually a field and would re-open
	 *  the software keyboard that blur closed. */
	private heldFocusAtCollapse = false

	/** Raise a notice. Every call is a fresh event: an alert already on screen
	 *  collapses and re-opens rather than rewriting itself in place. */
	raise(notice: SchmancyAlertNotice): void {
		this.raise$.next(notice)
	}

	/** Collapse whatever is raised. A no-op when nothing is. */
	clear(): void {
		this.raise$.next(null)
	}

	private focusIsInScope(): boolean {
		const focused = deepActiveElement()
		return (
			focused === null ||
			focused === document.body ||
			composedWithin(focused, this) ||
			composedWithin(focused, this.reportsOn ?? this.anchor)
		)
	}

	/** The alert and every element above it, crossing each shadow root to its host. */
	private ancestors(): HTMLElement[] {
		const ancestors: HTMLElement[] = [this]
		for (let at = this.parentNode; at !== null; at = at instanceof ShadowRoot ? at.host : at.parentNode) {
			if (at instanceof HTMLElement) ancestors.push(at)
		}
		return ancestors
	}

	/** Whether assistive tech and focus can reach the alert: no ancestor is
	 *  `inert`, as the page behind an open modal is. */
	private reachable(): boolean {
		return !this.ancestors().some(at => at.inert)
	}

	/** Emits once the alert is reachable. Delivery waits for the overlay to lift
	 *  the attribute rather than spending itself on a page no one can reach. */
	private exposed$(): Observable<unknown> {
		return this.reachable()
			? of(null)
			: merge(
					...this.ancestors().map(at => mutationObserver(at, { attributes: true, attributeFilter: ['inert'] })),
				).pipe(
					filter(() => this.reachable()),
					take(1),
				)
	}

	override connectedCallback() {
		super.connectedCallback()

		this.raise$
			.pipe(
				tap(() => {
					this.heldFocusAtCollapse = this.shadowRoot?.activeElement != null
					this.open = false
					this.announcement = null
				}),
				switchMap(notice =>
					(notice !== null
						? of(notice).pipe(
								tap(() => {
									const focused = deepActiveElement()
									if (this.focusIsInScope() && focused instanceof HTMLElement) focused.blur()
								}),
								switchMap(() => timer(this.shown === null || reducedMotion$.value ? 0 : REPLACE_COLLAPSE_MS)),
								// Copy and `open` land in the SAME render: `reveal` measures
								// its content at the frame boundary, after every nested
								// element's own first render, so a wrapper whose content
								// arrives with the flip opens to its real height.
								tap(() => {
									this.shown = notice
									this.shownReachable = this.reachable()
									this.open = true
								}),
								switchMap(() => from(this.updateComplete)),
								switchMap(() => {
									// One settle, two observers: the pin's `takeUntil` and the
									// delivery below must resolve the SAME event, or the two
									// arm separate `fromEvent` listeners and one of them waits
									// on a transition the other already consumed.
									const settled$ = this.settled$(true).pipe(share())
									return merge(
										// The trailing `of(null)` is a final pin after the settle:
										// `release()` clears the clamp (`maxHeight = ''`) in the
										// same tick it announces, so the natural height the panel
										// lands on can differ from the animated target.
										concat(animationFrames().pipe(takeUntil(settled$)), of(null)).pipe(
											tap(() => {
												// A page behind an open modal must not move under the dialog.
												const pageReachable = this.reachable()
												if (this.focusIsInScope()) {
													if (pageReachable) (this.anchor ?? this.panel.value)?.scrollIntoView(PIN)
												} else {
													if (pageReachable) this.panel.value?.scrollIntoView(PIN)
													deepActiveElement()?.scrollIntoView(PIN)
												}
											}),
											takeUntil(userScroll$),
											ignoreElements(),
										),
										settled$.pipe(
											switchMap(() => this.exposed$()),
											switchMap(() => {
												const assertive = ASSERTIVE_TONES.has(notice.tone)
												// Inserted where no one could hear it, the panel is a named
												// group, and this region is the refusal's only voice.
												const unheard = assertive && !this.shownReachable
												const announce$ = animationFrames().pipe(
													skip(1),
													take(1),
													tap(() => (this.announcement = { notice, politeness: assertive ? 'assertive' : 'polite' })),
												)
												return this.focusIsInScope()
													? merge(
															// preventScroll: focus()'s own implicit scroll targets the
															// PANEL, and would undo the pin's placement of the anchor.
															of(null).pipe(tap(() => this.panel.value?.focus({ preventScroll: true }))),
															unheard ? announce$ : EMPTY,
														)
													: merge(
															assertive && !unheard ? EMPTY : announce$,
															// A reader who comes back to the control this notice
															// reports on — Shift+Tab onto the anchor — may find the
															// panel above the fold; the control's own focus stops there.
															fromEvent(document, 'focusin').pipe(
																filter(() => composedWithin(deepActiveElement(), this.reportsOn ?? this.anchor)),
																take(1),
																tap(() => {
																	this.panel.value?.scrollIntoView(PIN)
																	deepActiveElement()?.scrollIntoView(PIN)
																}),
															),
														)
											}),
										),
									)
								}),
							)
						: this.shown === null
							? EMPTY
							: // The copy outlives the collapse so the panel actually shrinks
								// away rather than vanishing mid-animation.
								from(this.updateComplete).pipe(
									switchMap(() => this.settled$(false)),
									tap(() => {
										this.shown = null
										if (this.heldFocusAtCollapse) this.anchor?.focus()
										this.heldFocusAtCollapse = false
									}),
								)
					).pipe(
						// Per raise, inside the flatten: one notice's choreography
						// throwing (a ref torn down under it, a focus() into a detached
						// tree) must not terminate `raise$` for the rest of the
						// connection and swallow every LATER notice in silence — the
						// panel is open and readable by then; only the trailing polish
						// is lost.
						catchError(error => {
							console.warn('[SchmancyAlert] raise choreography failed', error)
							return EMPTY
						}),
					),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	override willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)
		this.toggleAttribute('raised', this.shown !== null)
	}

	/** `reveal`'s own completion signal for the transition just started — the
	 *  directive owns the backstop and the reduced-motion branch, so nothing
	 *  here races a hand-picked timer or re-derives when a transition will run.
	 *
	 *  `box` is a ref, so its value is typed `HTMLElement | undefined` and this
	 *  package's `strict: false` lets that `undefined` reach `fromEvent`, which
	 *  throws on it. The whole choreography runs inside `raise$`'s switchMap: a
	 *  throw there terminates `raise$` for the rest of the connection and every
	 *  later notice is silently swallowed, so the box's absence resolves the
	 *  step instead — a settle the element is already in. */
	private settled$(open: boolean): Observable<RevealSettledEvent | null> {
		if (!this.box.value) return of(null)
		return fromEvent<RevealSettledEvent>(this.box.value, 'reveal-settled').pipe(
			filter(e => e.detail.open === open),
			take(1),
		)
	}

	override render(): TemplateResult {
		const notice = this.shown
		const alertRole = notice !== null && ASSERTIVE_TONES.has(notice.tone) && this.shownReachable
		const words =
			this.announcement === null
				? ''
				: [this.announcement.notice.headline, this.announcement.notice.body].filter(Boolean).join(' ')

		return html`
			<div
				${ref(this.box)}
				${reveal(this.open, { preset: 'snappy' })}
				class="overflow-hidden [display:var(--schmancy-alert-box)]"
			>
				${when(notice !== null, () =>
					// Keyed on the notice, so every raise inserts a fresh panel and its
					// role is decided at insertion: an element whose role flips to
					// `alert` in place is not a reliable announcement.
					keyed(
						notice,
						html`
						<schmancy-surface
							${ref(this.panel)}
							type=${notice.tone}
							role=${alertRole ? 'alert' : 'group'}
							aria-live=${ifDefined(alertRole ? 'assertive' : undefined)}
							aria-labelledby=${ifDefined(alertRole ? undefined : 'alert-headline')}
							aria-describedby=${ifDefined(alertRole || notice.body === undefined ? undefined : 'alert-body')}
							tabindex="-1"
							class="grid gap-3 rounded-xl p-4 outline-none"
						>
							<div class="grid gap-1">
								<schmancy-typography id="alert-headline" type="title" token="lg">${notice.headline}</schmancy-typography>
								${when(
									notice.body !== undefined,
									() =>
										html`<schmancy-typography id="alert-body" type="body" token="md">${notice.body}</schmancy-typography>`,
								)}
							</div>
							${when(
								notice.remedy !== undefined,
								() => html`
									<schmancy-button
										variant="filled"
										color="neutral"
										size="sm"
										class="justify-self-start"
										@click=${() => this.dispatchEvent(new SchmancyAlertRemedyEvent(notice))}
									>
										${notice.remedy}
									</schmancy-button>
								`,
							)}
						</schmancy-surface>
					`,
					),
				)}
			</div>
			<!-- Outside the reveal box, which is inert while it collapses and would
			     hide both regions from assistive tech. -->
			<schmancy-visually-hidden role="status" aria-live="polite">
				${this.announcement?.politeness === 'polite' ? words : ''}
			</schmancy-visually-hidden>
			<schmancy-visually-hidden aria-live="assertive" aria-atomic="true">
				${this.announcement?.politeness === 'assertive' ? words : ''}
			</schmancy-visually-hidden>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-alert': SchmancyAlert
	}
	interface HTMLElementEventMap {
		remedy: SchmancyAlertRemedyEvent
	}
}
